/**
 * PremiumReportExport.jsx
 * Export reports to PDF, Excel, and CSV
 *
 * Design System Premium Fleet v2.0
 * Professional report export with styling
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Typography,
  alpha,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// PDF STYLES - Based on premium tokens
// ============================================================================

const getPdfStyles = (isDark = true) => ({
  // Header
  headerBg: isDark ? [13, 17, 23] : [255, 255, 255],
  headerText: isDark ? [255, 255, 255] : [17, 24, 39],

  // Table
  tableHeaderBg: isDark ? [22, 27, 34] : [243, 244, 246],
  tableHeaderText: isDark ? [230, 237, 243] : [17, 24, 39],
  tableRowBg: isDark ? [13, 17, 23] : [255, 255, 255],
  tableRowAltBg: isDark ? [22, 27, 34] : [249, 250, 251],
  tableText: isDark ? [230, 237, 243] : [55, 65, 81],
  tableBorder: isDark ? [48, 54, 61] : [209, 213, 219],

  // Accent
  accentColor: [0, 212, 170],

  // Font
  fontFamily: 'helvetica',
  fontSize: 10,
  headerFontSize: 18,
  subtitleFontSize: 11,
});

// ============================================================================
// PDF EXPORTER CLASS
// ============================================================================

export class PDFExporter {
  constructor(options = {}) {
    this.title = options.title || 'Report';
    this.subtitle = options.subtitle || '';
    this.logo = options.logo || null;
    this.orientation = options.orientation || 'portrait';
    this.pageSize = options.pageSize || 'a4';
    this.isDark = options.isDark !== false;
    this.styles = getPdfStyles(this.isDark);
    this.footer = options.footer || null;
    this.companyName = options.companyName || '';

    this.doc = new jsPDF({
      orientation: this.orientation,
      unit: 'mm',
      format: this.pageSize,
    });

    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 15;
    this.currentY = this.margin;

    this._addHeader();
  }

  _addHeader() {
    const { styles, doc, margin, pageWidth } = this;

    // Background header
    doc.setFillColor(...styles.headerBg);
    doc.rect(0, 0, pageWidth, 35, 'F');

    // Accent line
    doc.setFillColor(...styles.accentColor);
    doc.rect(0, 35, pageWidth, 1, 'F');

    // Title
    doc.setFont(styles.fontFamily, 'bold');
    doc.setFontSize(styles.headerFontSize);
    doc.setTextColor(...styles.headerText);
    doc.text(this.title, margin, 18);

    // Subtitle
    if (this.subtitle) {
      doc.setFont(styles.fontFamily, 'normal');
      doc.setFontSize(styles.subtitleFontSize);
      doc.setTextColor(...styles.headerText);
      doc.text(this.subtitle, margin, 26);
    }

    // Company name (right side)
    if (this.companyName) {
      doc.setFont(styles.fontFamily, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...styles.headerText);
      const textWidth = doc.getTextWidth(this.companyName);
      doc.text(this.companyName, pageWidth - margin - textWidth, 18);
    }

    // Generated date
    const dateStr = new Date().toLocaleString();
    doc.setFontSize(9);
    doc.setTextColor(...styles.tableText);
    const dateWidth = doc.getTextWidth(dateStr);
    doc.text(dateStr, pageWidth - margin - dateWidth, 26);

    this.currentY = 45;
  }

  _addFooter(pageNumber, totalPages) {
    const { doc, styles, margin, pageWidth, pageHeight } = this;

    // Footer line
    doc.setDrawColor(...styles.tableBorder);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    // Page number
    doc.setFont(styles.fontFamily, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...styles.tableText);
    const pageText = `${pageNumber} / ${totalPages}`;
    const textWidth = doc.getTextWidth(pageText);
    doc.text(pageText, (pageWidth - textWidth) / 2, pageHeight - 8);

    // Custom footer text
    if (this.footer) {
      doc.text(this.footer, margin, pageHeight - 8);
    }
  }

  addTable(config) {
    const { columns, data, options = {} } = config;
    const { styles, doc, margin, pageWidth } = this;

    const tableColumns = columns.map((col) => ({
      header: col.header,
      dataKey: col.dataKey || col.key,
    }));

    const tableData = data.map((row) => {
      const formattedRow = {};
      columns.forEach((col) => {
        const key = col.dataKey || col.key;
        let value = row[key];
        if (col.format && typeof col.format === 'function') {
          value = col.format(value, row);
        }
        formattedRow[key] = value ?? '';
      });
      return formattedRow;
    });

    autoTable(doc, {
      startY: this.currentY,
      head: [tableColumns.map((c) => c.header)],
      body: tableData.map((row) => tableColumns.map((c) => row[c.dataKey])),
      margin: { left: margin, right: margin },
      styles: {
        font: styles.fontFamily,
        fontSize: styles.fontSize,
        textColor: styles.tableText,
        lineColor: styles.tableBorder,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: styles.tableHeaderBg,
        textColor: styles.tableHeaderText,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fillColor: styles.tableRowBg,
      },
      alternateRowStyles: {
        fillColor: styles.tableRowAltBg,
      },
      columnStyles: columns.reduce((acc, col, index) => {
        acc[index] = {
          halign: col.align || 'left',
          cellWidth: col.width ? col.width * 2 : 'auto',
        };
        return acc;
      }, {}),
      ...options,
    });

    this.currentY = doc.lastAutoTable.finalY + 10;
    return this;
  }

  addText(text, options = {}) {
    const { styles, doc, margin, pageWidth } = this;
    const {
      fontSize = styles.fontSize,
      fontStyle = 'normal',
      color = styles.tableText,
      align = 'left',
      marginTop = 5,
      marginBottom = 5,
    } = options;

    this.currentY += marginTop;

    doc.setFont(styles.fontFamily, fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);

    const maxWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(text, maxWidth);

    let x = margin;
    if (align === 'center') {
      x = pageWidth / 2;
    } else if (align === 'right') {
      x = pageWidth - margin;
    }

    doc.text(lines, x, this.currentY, { align });
    this.currentY += lines.length * (fontSize * 0.4) + marginBottom;

    return this;
  }

  addSummary(items) {
    const { styles, doc, margin } = this;

    this.addText('Summary', {
      fontSize: 12,
      fontStyle: 'bold',
      marginTop: 10,
      marginBottom: 5,
    });

    items.forEach((item) => {
      doc.setFont(styles.fontFamily, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...styles.tableText);
      doc.text(`${item.label}: `, margin, this.currentY);

      const labelWidth = doc.getTextWidth(`${item.label}: `);
      doc.setFont(styles.fontFamily, 'bold');
      doc.text(String(item.value), margin + labelWidth, this.currentY);

      this.currentY += 6;
    });

    this.currentY += 5;
    return this;
  }

  addChart(canvasOrImage, options = {}) {
    const { doc, margin, pageWidth } = this;
    const { width = 150, height = 80, align = 'center' } = options;

    let imgData;
    if (canvasOrImage instanceof HTMLCanvasElement) {
      imgData = canvasOrImage.toDataURL('image/png');
    } else if (typeof canvasOrImage === 'string') {
      imgData = canvasOrImage;
    } else {
      return this;
    }

    let x = margin;
    if (align === 'center') {
      x = (pageWidth - width) / 2;
    } else if (align === 'right') {
      x = pageWidth - margin - width;
    }

    doc.addImage(imgData, 'PNG', x, this.currentY, width, height);
    this.currentY += height + 10;

    return this;
  }

  addPageBreak() {
    this.doc.addPage();
    this._addHeader();
    return this;
  }

  addTotalRecords(count, label = 'Total') {
    this.addText(`${label}: ${count.toLocaleString()} records`, {
      fontSize: 10,
      fontStyle: 'italic',
      color: this.styles.tableText,
      align: 'right',
      marginTop: 2,
      marginBottom: 10,
    });
    return this;
  }

  save(filename = 'report.pdf') {
    // Add footers to all pages
    const totalPages = this.doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      this._addFooter(i, totalPages);
    }

    this.doc.save(filename);
    return this;
  }

  getBlob() {
    const totalPages = this.doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      this._addFooter(i, totalPages);
    }
    return this.doc.output('blob');
  }
}

// ============================================================================
// EXCEL EXPORTER CLASS
// ============================================================================

export class ExcelExporter {
  constructor(options = {}) {
    this.filename = options.filename || 'report';
    this.workbook = XLSX.utils.book_new();
    this.sheets = [];
  }

  addSheet(name, config) {
    const { columns, data, options = {} } = config;

    // Create header row
    const headers = columns.map((col) => col.header);

    // Create data rows
    const rows = data.map((row) => columns.map((col) => {
      const key = col.dataKey || col.key;
      let value = row[key];
      if (col.format && typeof col.format === 'function') {
        value = col.format(value, row);
      }
      return value ?? '';
    }));

    // Create worksheet
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = columns.map((col) => ({
      wch: col.width || 15,
    }));
    ws['!cols'] = colWidths;

    // Style header (bold) - Note: xlsx doesn't support styling in free version
    // For production, consider using xlsx-style or exceljs

    // Add to workbook
    XLSX.utils.book_append_sheet(this.workbook, ws, name.substring(0, 31));
    this.sheets.push({ name, ws });

    return this;
  }

  addSummarySheet(name, items) {
    const data = items.map((item) => ({
      metric: item.label,
      value: item.value,
    }));

    return this.addSheet(name, {
      columns: [
        { header: 'Metric', key: 'metric', width: 30 },
        { header: 'Value', key: 'value', width: 20 },
      ],
      data,
    });
  }

  save(filename) {
    const finalFilename = filename || `${this.filename}.xlsx`;
    const wbout = XLSX.write(this.workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, finalFilename.endsWith('.xlsx') ? finalFilename : `${finalFilename}.xlsx`);
    return this;
  }

  getBlob() {
    const wbout = XLSX.write(this.workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    return new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
}

// ============================================================================
// CSV EXPORTER
// ============================================================================

export const CSVExporter = {
  export(data, columns, filename = 'report.csv') {
    // Create header
    const headers = columns.map((col) => col.header);

    // Create rows
    const rows = data.map((row) => columns.map((col) => {
      const key = col.dataKey || col.key;
      let value = row[key];
      if (col.format && typeof col.format === 'function') {
        value = col.format(value, row);
      }
      // Escape quotes and wrap in quotes if contains comma
      if (typeof value === 'string') {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
      }
      return value ?? '';
    }));

    // Combine
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
  },

  getBlob(data, columns) {
    const headers = columns.map((col) => col.header);
    const rows = data.map((row) => columns.map((col) => {
      const key = col.dataKey || col.key;
      let value = row[key];
      if (col.format && typeof col.format === 'function') {
        value = col.format(value, row);
      }
      if (typeof value === 'string') {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
      }
      return value ?? '';
    }));

    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    return new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const formatDataForExport = (data, columns) => columns.map((col) => {
  const key = col.dataKey || col.key;
  return {
    ...col,
    getValue: (row) => {
      let value = row[key];
      if (col.format && typeof col.format === 'function') {
        value = col.format(value, row);
      }
      return value ?? '';
    },
  };
});

export const generateFilename = (prefix, extension) => {
  const date = new Date();
  const dateStr = date.toISOString().split('T')[0];
  const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
  return `${prefix}-${dateStr}-${timeStr}.${extension}`;
};

// ============================================================================
// USE REPORT EXPORT HOOK
// ============================================================================

export const useReportExport = (options = {}) => {
  const {
    data = [],
    columns = [],
    title = 'Report',
    subtitle = '',
    filename = 'report',
    companyName = '',
  } = options;

  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const exportPDF = useCallback(async (exportOptions = {}) => {
    setIsExporting(true);
    setProgress(0);
    setError(null);

    try {
      setProgress(20);

      const exporter = new PDFExporter({
        title: exportOptions.title || title,
        subtitle: exportOptions.subtitle || subtitle,
        orientation: exportOptions.orientation || (columns.length > 5 ? 'landscape' : 'portrait'),
        companyName: exportOptions.companyName || companyName,
        isDark: exportOptions.isDark !== false,
      });

      setProgress(40);

      exporter.addTable({
        columns,
        data: exportOptions.data || data,
      });

      setProgress(70);

      exporter.addTotalRecords((exportOptions.data || data).length);

      setProgress(90);

      const outputFilename = exportOptions.filename || generateFilename(filename, 'pdf');
      exporter.save(outputFilename);

      setProgress(100);
      return { success: true, filename: outputFilename };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsExporting(false);
    }
  }, [data, columns, title, subtitle, filename, companyName]);

  const exportExcel = useCallback(async (exportOptions = {}) => {
    setIsExporting(true);
    setProgress(0);
    setError(null);

    try {
      setProgress(20);

      const exporter = new ExcelExporter({
        filename: exportOptions.filename || filename,
      });

      setProgress(40);

      exporter.addSheet(exportOptions.sheetName || 'Data', {
        columns,
        data: exportOptions.data || data,
      });

      setProgress(70);

      // Add summary sheet if requested
      if (exportOptions.summary) {
        exporter.addSummarySheet('Summary', exportOptions.summary);
      }

      setProgress(90);

      const outputFilename = exportOptions.filename || generateFilename(filename, 'xlsx');
      exporter.save(outputFilename);

      setProgress(100);
      return { success: true, filename: outputFilename };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsExporting(false);
    }
  }, [data, columns, filename]);

  const exportCSV = useCallback(async (exportOptions = {}) => {
    setIsExporting(true);
    setProgress(0);
    setError(null);

    try {
      setProgress(50);

      const outputFilename = exportOptions.filename || generateFilename(filename, 'csv');
      CSVExporter.export(exportOptions.data || data, columns, outputFilename);

      setProgress(100);
      return { success: true, filename: outputFilename };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsExporting(false);
    }
  }, [data, columns, filename]);

  return {
    exportPDF,
    exportExcel,
    exportCSV,
    isExporting,
    progress,
    error,
  };
};

// ============================================================================
// EXPORT DROPDOWN COMPONENT
// ============================================================================

export const ExportDropdown = ({
  onExportPDF,
  onExportExcel,
  onExportCSV,
  formats = ['pdf', 'xlsx', 'csv'],
  disabled = false,
  loading = false,
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (format) => {
    handleClose();
    switch (format) {
      case 'pdf':
        onExportPDF?.();
        break;
      case 'xlsx':
        onExportExcel?.();
        break;
      case 'csv':
        onExportCSV?.();
        break;
      default:
        break;
    }
  };

  const formatConfig = {
    pdf: { icon: <PdfIcon />, label: t('exportPDF'), color: '#ef4444' },
    xlsx: { icon: <ExcelIcon />, label: t('exportExcel'), color: '#22c55e' },
    csv: { icon: <CsvIcon />, label: t('exportCSV'), color: '#3b82f6' },
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled || loading}
        endIcon={loading ? <CircularProgress size={16} /> : <ArrowDownIcon />}
        startIcon={<DownloadIcon />}
        sx={{
          borderColor: tokens?.border?.default || '#374151',
          color: tokens?.text?.primary || '#f9fafb',
          '&:hover': {
            borderColor: tokens?.brand?.primary || '#6366f1',
            bgcolor: alpha(tokens?.brand?.primary || '#6366f1', 0.1),
          },
        }}
      >
        {t('exportTitle')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: tokens?.surface?.secondary || '#1f2937',
            border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
            minWidth: 180,
          },
        }}
      >
        {formats.map((format) => {
          const config = formatConfig[format];
          if (!config) return null;
          return (
            <MenuItem
              key={format}
              onClick={() => handleExport(format)}
              sx={{
                color: tokens?.text?.primary || '#f9fafb',
                '&:hover': {
                  bgcolor: tokens?.surface?.hover || alpha('#fff', 0.05),
                },
              }}
            >
              <ListItemIcon sx={{ color: config.color }}>
                {config.icon}
              </ListItemIcon>
              <ListItemText>{config.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// ============================================================================
// EXPORT BUTTON COMPONENT (Main)
// ============================================================================

const ExportButton = ({
  data = [],
  columns = [],
  filename = 'report',
  title = 'Report',
  subtitle = '',
  formats = ['pdf', 'xlsx', 'csv'],
  onExport,
  disabled = false,
  companyName = '',
  variant = 'dropdown', // 'dropdown', 'split', 'single'
  defaultFormat = 'pdf',
  size = 'medium',
  color = 'primary',
}) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [lastExported, setLastExported] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (variant === 'single') {
      handleExport(defaultFormat);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (format) => {
    handleClose();
    setIsExporting(true);

    try {
      const exportFilename = generateFilename(filename, format === 'xlsx' ? 'xlsx' : format);

      switch (format) {
        case 'pdf': {
          const exporter = new PDFExporter({
            title,
            subtitle,
            orientation: columns.length > 5 ? 'landscape' : 'portrait',
            companyName,
          });
          exporter.addTable({ columns, data });
          exporter.addTotalRecords(data.length);
          exporter.save(exportFilename);
          break;
        }
        case 'xlsx': {
          const exporter = new ExcelExporter({ filename });
          exporter.addSheet('Data', { columns, data });
          exporter.save(exportFilename);
          break;
        }
        case 'csv': {
          CSVExporter.export(data, columns, exportFilename);
          break;
        }
        default:
          break;
      }

      setLastExported(format);
      onExport?.(format, exportFilename);

      // Reset success indicator after 2s
      setTimeout(() => setLastExported(null), 2000);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const formatConfig = {
    pdf: { icon: <PdfIcon />, label: t('exportPDF'), color: '#ef4444' },
    xlsx: { icon: <ExcelIcon />, label: t('exportExcel'), color: '#22c55e' },
    csv: { icon: <CsvIcon />, label: t('exportCSV'), color: '#3b82f6' },
  };

  const buttonContent = (
    <>
      {isExporting ? (
        <CircularProgress size={18} sx={{ mr: 1 }} />
      ) : lastExported ? (
        <CheckIcon sx={{ mr: 1, color: tokens?.semantic?.success || '#22c55e' }} />
      ) : (
        <DownloadIcon sx={{ mr: 1 }} />
      )}
      {variant === 'single' ? formatConfig[defaultFormat]?.label : t('exportTitle')}
      {variant !== 'single' && <ArrowDownIcon sx={{ ml: 0.5 }} />}
    </>
  );

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled || isExporting || data.length === 0}
        size={size}
        sx={{
          borderColor: lastExported
            ? tokens?.semantic?.success || '#22c55e'
            : tokens?.border?.default || '#374151',
          color: lastExported
            ? tokens?.semantic?.success || '#22c55e'
            : tokens?.text?.primary || '#f9fafb',
          transition: 'all 200ms ease',
          '&:hover': {
            borderColor: tokens?.brand?.primary || '#6366f1',
            bgcolor: alpha(tokens?.brand?.primary || '#6366f1', 0.1),
          },
        }}
      >
        {buttonContent}
      </Button>

      {variant !== 'single' && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1,
              bgcolor: tokens?.surface?.secondary || '#1f2937',
              border: `1px solid ${tokens?.border?.subtle || '#374151'}`,
              borderRadius: tokens?.radius?.md || 8,
              minWidth: 200,
              boxShadow: tokens?.shadows?.lg || '0 10px 15px -3px rgba(0,0,0,0.3)',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: tokens?.text?.tertiary || '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('exportTitle')}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: tokens?.border?.subtle || '#374151' }} />

          {formats.map((format) => {
            const config = formatConfig[format];
            if (!config) return null;
            return (
              <MenuItem
                key={format}
                onClick={() => handleExport(format)}
                sx={{
                  py: 1.5,
                  color: tokens?.text?.primary || '#f9fafb',
                  '&:hover': {
                    bgcolor: tokens?.surface?.hover || alpha('#fff', 0.05),
                  },
                }}
              >
                <ListItemIcon sx={{ color: config.color, minWidth: 36 }}>
                  {config.icon}
                </ListItemIcon>
                <ListItemText
                  primary={config.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            );
          })}

          {data.length > 0 && [
              <Divider key="divider-count" sx={{ borderColor: tokens?.border?.subtle || '#374151' }} />,
              <Box key="box-count" sx={{ px: 2, py: 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: tokens?.text?.tertiary || '#6b7280',
                  }}
                >
                  {data.length.toLocaleString()} {t('exportTotalRecords').replace('{count}', '').replace('records', '').trim()}
                </Typography>
              </Box>,
          ]}
        </Menu>
      )}
    </>
  );
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default ExportButton;
